'use client';

import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  Clusterer,
  Map,
  Placemark,
  YMaps,
  ZoomControl,
} from '@iminside/react-yandex-maps';
import type { RealEstateCatalogResponseT } from '@/forApp/types/Entity';
import { getRouteHref, ROUTES } from '@/forApp/utils/routes';

import styles from '@/app/maps/[type]/styles.module.css';

export type MapPaneProps = {
  catalog: RealEstateCatalogResponseT | null;
  onBoundsChange?: (bounds: [[number, number], [number, number]]) => void;
  onSelect?: (ids: Array<string | number>) => void;
  selectedIds?: Array<string | number>;
  isMobile?: boolean;
};

const MapPane: React.FC<MapPaneProps> = ({
  catalog,
  onBoundsChange,
  onSelect,
  selectedIds = [],
  isMobile,
}) => {
  const [ymapsInstance, setYmapsInstance] = useState<any>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const didFitOnDataRef = useRef<boolean>(false);
  const highlightedClusterRef = useRef<any>(null);

  const handleMapLoad = (ymaps: any) => setYmapsInstance(ymaps);

  const handleBoundsChange = (e: any) => {
    if (!onBoundsChange) return;
    const newBounds =
      e?.get?.('newBounds') || e?.originalEvent?.newBounds || e?.newBounds;
    if (!newBounds || !Array.isArray(newBounds) || newBounds.length !== 2)
      return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      try {
        onBoundsChange(newBounds as [[number, number], [number, number]]);
      } catch {
        // noop
      }
    }, 500);
  };

  // Clear selection when clicking on empty map area
  const handleMapClick = () => {
    try {
      onSelect?.([]);
    } catch {}
    // reset highlighted cluster style if any
    if (highlightedClusterRef.current) {
      try {
        highlightedClusterRef.current.options.set(
          'preset',
          'islands#invertedDarkClusterIcons',
        );
      } catch {}
      highlightedClusterRef.current = null;
    }
  };

  // Fit bounds to show items. On mobile: do this on every catalog change; on desktop: only once on initial data load.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const coords = extractValidCoords(catalog);
    if (!coords.length) return;

    // Desktop keeps first-fit behavior; Mobile always fits on catalog changes
    if (!isMobile && didFitOnDataRef.current) return;

    try {
      if (coords.length === 1) {
        const [lat, lon] = coords[0];
        map.setCenter([lat, lon], 14, { duration: 300 });
      } else {
        const b = boundsFromCoords(coords);
        if (b) {
          map.setBounds(b, {
            checkZoomRange: true,
            duration: 300,
            useMapMargin: true,
          });
        }
      }
      // Only lock the first fit for desktop
      if (!isMobile) didFitOnDataRef.current = true;
    } catch {}
  }, [catalog, isMobile]);

  const selectedIdSet = new Set((selectedIds || []).map((v) => String(v)));

  return (
    <div className={styles.mapPane}>
      <YMaps>
        <Map
          className={styles.map}
          defaultState={mapStateFromCatalog(catalog)}
          modules={['control.ZoomControl']}
          onLoad={handleMapLoad}
          onBoundsChange={handleBoundsChange}
          onClick={handleMapClick}
          instanceRef={(ref: any) => {
            if (ref) mapInstanceRef.current = ref;
          }}
        >
          {isMobile ? (
            <ZoomControl
              options={{ size: 'small', position: { right: 12, bottom: 45 } }}
            />
          ) : null}
          <Clusterer
            onClick={(e: any) => {
              try {
                const cluster = e?.get?.('target');
                // Reset previous highlighted cluster if different
                if (
                  highlightedClusterRef.current &&
                  highlightedClusterRef.current !== cluster
                ) {
                  try {
                    highlightedClusterRef.current.options.set(
                      'preset',
                      'islands#invertedDarkClusterIcons',
                    );
                  } catch {}
                }
                // Highlight the clicked cluster only
                highlightedClusterRef.current = cluster;
                try {
                  cluster?.options?.set?.('preset', 'islands#redClusterIcons');
                } catch {}

                const gos = cluster?.getGeoObjects?.() || [];
                const ids = gos
                  .map((g: any) => g?.properties?.get?.('itemId'))
                  .filter((x: any) => x !== undefined && x !== null);
                if (ids.length) onSelect?.(ids);

                // Custom zoom-in behavior on cluster click
                const map = mapInstanceRef.current;
                if (map) {
                  // Try to get cluster bounds to compute center
                  let center: [number, number] | null = null;
                  try {
                    const b = cluster?.geometry?.getBounds?.();
                    if (b && Array.isArray(b) && b.length === 2) {
                      center = [
                        (b[0][0] + b[1][0]) / 2,
                        (b[0][1] + b[1][1]) / 2,
                      ];
                    }
                  } catch {}

                  if (!center) {
                    // Fallback: compute average of child placemark coordinates
                    try {
                      const coords: [number, number][] = gos
                        .map((g: any) => g?.geometry?.getCoordinates?.())
                        .filter(
                          (c: any) =>
                            Array.isArray(c) &&
                            isFinite(c[0]) &&
                            isFinite(c[1]),
                        );
                      if (coords.length) {
                        const sum = coords.reduce(
                          (acc, c) => [acc[0] + c[0], acc[1] + c[1]],
                          [0, 0],
                        );
                        center = [
                          sum[0] / coords.length,
                          sum[1] / coords.length,
                        ] as [number, number];
                      }
                    } catch {}
                  }

                  if (center) {
                    const curZoom = Number(map?.getZoom?.()) || 10;
                    const nextZoom = Math.min(curZoom + 2, 18);
                    try {
                      map.setCenter(center, nextZoom, { duration: 300 });
                    } catch {}
                  }
                }
              } catch {}
            }}
            options={{
              preset: 'islands#invertedDarkClusterIcons',
              clusterize: true,
              gridSize: 800,
              clusterDisableClickZoom: true,
              clusterOpenBalloonOnClick: false,
              zoomMargin: 200,
              groupByCoordinates: false,
              clusterIconColor: '#2A2A2A',
              clusterIconContentLayout:
                ymapsInstance?.templateLayoutFactory?.createClass(
                  '<div style="color: white; font-weight: bold; text-align: center; line-height: 46px;">$[properties.geoObjects.length]</div>',
                ),
            }}
          >
            {catalog?.entities?.data?.map((item) => {
              const lat = parseFloat(item.latitude || '0');
              const lon = parseFloat(item.longitude || '0');
              if (!isFinite(lat) || !isFinite(lon) || (lat === 0 && lon === 0))
                return null;
              const isSelected = selectedIdSet.has(String(item.id));
              return (
                <Placemark
                  key={item.id}
                  geometry={[lat, lon]}
                  properties={{ itemId: item.id }}
                  options={{
                    iconLayout: 'default#image',
                    iconImageHref: isSelected
                      ? '/icons/ActiveMapPoint.svg'
                      : '/icons/NoActiveMapPoint.svg',
                    iconImageSize: [64, 64],
                    iconImageOffset: [-32, -64],
                    zIndex: 1,
                  }}
                  onClick={() => {
                    // Clear any cluster highlight when selecting a single placemark
                    if (highlightedClusterRef.current) {
                      try {
                        highlightedClusterRef.current.options.set(
                          'preset',
                          'islands#invertedDarkClusterIcons',
                        );
                      } catch {}
                      highlightedClusterRef.current = null;
                    }
                    // On mobile: open the listing in a new tab
                    if (isMobile) {
                      try {
                        const typeSlug =
                          catalog?.slug ||
                          (item?.entity_type?.slug as string) ||
                          '';
                        // const cityName = encodeURIComponent(item?.city?.name || '');
                        // const adType = encodeURIComponent(item?.ad_type || '');
                        const slug = encodeURIComponent(item?.slug || '');
                        if (typeSlug && slug) {
                          const url = getRouteHref(ROUTES.ENTITY, {
                            type: typeSlug,
                            slug,
                          });
                          window.open(url, '_blank');
                          return;
                        }
                      } catch {}
                    }
                    try {
                      onSelect?.([item.id]);
                    } catch {}
                  }}
                />
              );
            })}
          </Clusterer>
        </Map>
      </YMaps>
    </div>
  );
};

function centerFromCatalog(
  cat: RealEstateCatalogResponseT | null | undefined,
): [number, number] {
  const fallback: [number, number] = [51.185268, 58.654993];
  const coords = extractValidCoords(cat);
  if (coords.length === 0) return fallback;
  const [[minLat, minLon], [maxLat, maxLon]] = boundsFromCoords(coords)!;
  return [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
}

function mapStateFromCatalog(
  cat: RealEstateCatalogResponseT | null | undefined,
): any {
  const coords = extractValidCoords(cat);
  if (coords.length >= 1) {
    const b = boundsFromCoords(coords);
    if (b) return { bounds: b };
  }
  return { center: centerFromCatalog(cat), zoom: 10 };
}

function extractValidCoords(
  cat: RealEstateCatalogResponseT | null | undefined,
): [number, number][] {
  const items = cat?.entities?.data || [];
  const res: [number, number][] = [];
  for (const e of items) {
    const lat = parseFloat(e.latitude || '0');
    const lon = parseFloat(e.longitude || '0');
    if (isFinite(lat) && isFinite(lon) && !(lat === 0 && lon === 0))
      res.push([lat, lon]);
  }
  return res;
}

function boundsFromCoords(
  coords: [number, number][],
): [[number, number], [number, number]] | null {
  if (!coords.length) return null;
  let minLat = coords[0][0],
    maxLat = coords[0][0];
  let minLon = coords[0][1],
    maxLon = coords[0][1];
  for (let i = 1; i < coords.length; i++) {
    const [lat, lon] = coords[i];
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  return [
    [minLat, minLon],
    [maxLat, maxLon],
  ];
}

export default MapPane;
